import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Send, Target, Loader2, X, CalendarPlus } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import { useAuthStore } from '../../store/authStore';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { ROOKIE_LIMITS, subscriptionService } from '../../services/subscriptionService';
import PageHeader from '../layout/PageHeader';
import { CoachSteveGamePlanForm } from './CoachSteveGamePlanForm';
import { CoachSteveCreateGameForm } from './CoachSteveCreateGameForm';
import { playerCardService } from '../../services/playerCardService';
import { buildTeamContextForAgent, buildGameStatsContextForAgent, buildGameResultsContextForAgent } from './coachSteveAgentContext';
import { supabase } from '../../lib/supabase';
import type { Game } from '../../services/gameService';
import { useCoachSteveSession } from '../../hooks/useCoachSteveSession';
import {
  isCreatePracticeIntent,
  isAddDrillToPracticeIntent,
  supersedesPendingPlayerCreateWithPracticeRequest,
  isCreateGamePlanIntent,
  isCreateGameIntent,
  isCreateFormationIntent,
  isAssignPlayerToFormationIntent,
  isGameLineupCoachIntent,
  isCreatePlayerIntent,
  isSetInjuryIntent,
  isStatsLookupIntent,
  statsAgent,
  isAttendanceLookupIntent,
  attendanceAgent,
  startPracticePlaceholderFlow,
  continuePendingPracticeFocus,
  continuePendingAddDrillFollowUp,
  startCreateGameFlow,
  runAddPlayer,
  runInjuryStatusFlow,
  continuePendingPlayerCreate,
  continuePendingGameLineupPick,
  runHelpAgent,
  practiceAgent,
  gamePlanAgent,
  formationAgent,
  depthChartAgent,
  routeUserMessage,
  isRouterEnabled,
  logRouted,
} from './agents';
import type {
  AgentRunContext,
  ChatMessage,
  MessageRole,
  RouterIntent,
} from './agents';

export type { MessageRole, ChatMessage };

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: `Hey, I'm Coach Steve. Ask me anything about practices, drills, or game planning — or say **create a practice plan**, **create a game plan**, or **create a game** when you're ready and I'll walk you through it.\n\nYou can **add a player** (e.g. "Add John Doe as a sub. He can play infield and outfield.") or **set injury status** (e.g. "Mark John Doe out", "set #5 as questionable"), or **add a game** to the schedule. If you don’t tell me the player’s position groups, I’ll ask.`,
  createdAt: new Date(),
};

export interface CoachSteveProps {
  /** When true, render as a side panel (no page header, compact header with close) */
  isPanel?: boolean;
  /** Called when user closes the panel */
  onClose?: () => void;
}

export const CoachSteve: React.FC<CoachSteveProps> = ({ isPanel = false, onClose }) => {
  const { user } = useAuthStore();
  const { currentTeam } = useTeamStore();
  const { checkFeatureAccess } = useFeatureAccess();
  const session = useCoachSteveSession();
  const {
    pendingAction,
    createGameInitialValues,
    pendingPracticeForFocus,
    pendingAddDrillFollowUp,
    pendingGameLineupPick,
    pendingPlayerCreate,
    pendingInjuryUpdate,
    setPendingAction,
    setCreateGameInitialValues,
    setPendingPracticeForFocus,
    setPendingAddDrillFollowUp,
    setPendingGameLineupPick,
    setPendingPlayerCreate,
    setPendingInjuryUpdate,
    cancelFlow,
    pendingInterrupt,
    requestInterrupt,
    confirmInterrupt,
    pendingFlowLabel,
  } = session;
  const [messages, setMessages] = useState<ChatMessage[]>(() => [WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string | null>(null);
  const [teamContextForAgent, setTeamContextForAgent] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messageIdCounterRef = useRef(0);
  // Stores a secondary intent from a compound message; processed after the primary pending flow completes.
  const pendingFollowUpRef = useRef<string | null>(null);
  // Sub-agent to dispatch the follow-up to directly (bypasses re-routing).
  const pendingFollowUpAgentRef = useRef<string | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingAction, generationProgress]);

  // Restore focus to the input after each response completes.
  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  // Load live team roster + attributes for agent context (so Coach Steve can use app data for lineups, positions, etc.)
  useEffect(() => {
    if (!currentTeam?.id) {
      setTeamContextForAgent(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refreshTeamContextForAgent();
      } catch {
        if (!cancelled) setTeamContextForAgent(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam?.id]);

  const addMessage = (role: MessageRole, content: string, link?: { label: string; href: string }) => {
    const id = `msg-${Date.now()}-${++messageIdCounterRef.current}`;
    setMessages((prev) => [
      ...prev,
      {
        id,
        role,
        content,
        createdAt: new Date(),
        link,
      },
    ]);
  };

  const refreshTeamContextForAgent = async () => {
    if (!currentTeam?.id) {
      setTeamContextForAgent(null);
      return;
    }
    try {
      const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
      if (cards.length > 0) {
        let context = buildTeamContextForAgent(currentTeam, { cardsWithPlayers: cards });
        const [gameStatsBlock, gameResultsBlock] = await Promise.all([
          buildGameStatsContextForAgent(currentTeam.id, currentTeam.sport, cards, currentTeam.innings),
          buildGameResultsContextForAgent(currentTeam.id, currentTeam.sport),
        ]);
        if (gameResultsBlock) context = context + '\n\n' + gameResultsBlock;
        if (gameStatsBlock) context = context + '\n\n' + gameStatsBlock;
        setTeamContextForAgent(context);
        return;
      }
      const [{ data: players }, gameResultsBlock] = await Promise.all([
        supabase
          .from('players')
          .select('id, first_name, last_name, number, position, height, weight, speed, strength, experience, injury_status, batting_hand, batting_average, on_base_percentage')
          .eq('team_id', currentTeam.id)
          .order('last_name'),
        buildGameResultsContextForAgent(currentTeam.id, currentTeam.sport),
      ]);
      let context = buildTeamContextForAgent(currentTeam, { playersOnly: players ?? [] });
      if (gameResultsBlock) context = context + '\n\n' + gameResultsBlock;
      setTeamContextForAgent(context);
    } catch {
      setTeamContextForAgent(null);
    }
  };

  const handleQuickAction = async (action: 'practice' | 'game_plan') => {
    if (!currentTeam) {
      addMessage('assistant', "Please select a team first from the dashboard so I know which team we're planning for.");
      return;
    }
    if (action === 'game_plan' && currentTeam.sport !== 'Football') {
      addMessage('assistant', "Game plans are only available for Football. I can help you create a **practice plan** instead.");
      return;
    }
    if (action === 'practice' && !checkFeatureAccess('AI-powered practice planning')) {
      addMessage('assistant', "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more.");
      return;
    }
    if (action === 'game_plan' && !checkFeatureAccess('AI-powered game planning')) {
      addMessage('assistant', "AI-powered game planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more.");
      return;
    }
    if (action === 'practice') {
      addMessage('user', 'Create a practice plan');
      // Route through the agent context so Pro/feature checks stay
      // centralized inside the practice agent flow.
      await startPracticePlaceholderFlow(buildAgentContext(), 'Create a practice plan');
      return;
    }
    addMessage('user', 'Create a game plan');
    setPendingAction(action);
  };

  const handleFormSuccess = (
    action: 'practice' | 'game_plan',
    result: { id: string; label: string; href: string }
  ) => {
    setPendingAction(null);
    addMessage('assistant', `Done. I've created your ${action === 'practice' ? 'practice' : 'game plan'}. You can open it below.`, {
      label: result.label,
      href: result.href,
    });
  };

  const handleFormCancel = () => {
    setPendingAction(null);
    addMessage('assistant', 'No problem. Tell me what you’d like to do next, or use the shortcuts above.');
  };

  /**
   * Build the AgentRunContext that sub-agents need. Captures the latest
   * state via closures so handlers see fresh values without prop drilling.
   * Callers must guard `currentTeam` themselves where required.
   */
  const buildAgentContext = (): AgentRunContext => ({
    // currentTeam may be null at call time; agent dispatch checks it
    // beforehand and we cast to satisfy the type.
    team: currentTeam as NonNullable<typeof currentTeam>,
    userId: user?.id ?? null,
    recentMessages: messages,
    teamContextForAgent,
    pendingAction,
    pendingPlayerCreate,
    pendingInjuryUpdate,
    pendingPracticeForFocus,
    pendingAddDrillFollowUp,
    pendingGameLineupPick,
    addMessage,
    setIsLoading,
    setGenerationProgress,
    setPendingAction,
    setCreateGameInitialValues,
    setPendingPlayerCreate,
    setPendingInjuryUpdate,
    setPendingPracticeForFocus,
    setPendingAddDrillFollowUp,
    setPendingGameLineupPick,
    refreshTeamContextForAgent,
    checkFeatureAccess,
    handleQuickAction,
  });

  /**
   * Rookie monthly AI-chat-message gate, checked once by `handleSend` before
   * dispatching to ANY subagent — not just the general-Q&A help agent. This is
   * the single place that decides whether Coach Steve engages with a message
   * at all, so it's also the single place responsible for counting usage
   * (see `trackChatUsage`) rather than leaving each subagent to count itself.
   */
  const hasReachedAiChatLimit = async (): Promise<boolean> => {
    if (!user?.id) return false;
    await subscriptionService.ensureUsagePeriodResetted();
    const profile = await subscriptionService.getUserProfile(user.id);
    return subscriptionService.hasReachedChatLimit(profile);
  };

  /** Count one AI Coach interaction. Called by the orchestrator (`handleSend`) after a subagent actually ran — never speculatively. */
  const trackChatUsage = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      await subscriptionService.incrementUsageCounter('ai_chat_message_count');
    } catch (error) {
      console.error('Error incrementing AI chat message count:', error);
    }
  };

  /**
   * Compact label describing the active pending flow so the router prompt
   * can flag domain switches as `interrupt`. Pulled directly from the
   * session hook so every consumer agrees on what's pending.
   */
  const currentPendingFlowLabel = (): string | null => pendingFlowLabel;

  /**
   * If a follow-up text was queued by a compound-message router intent,
   * refresh team context and route the follow-up through the standard path.
   * `resolvedPlayerName` — if the primary flow just added a player, pass
   * their full name so the downstream agent knows "him"/"her" refers to them.
   */
  const processQueuedFollowUp = async (resolvedPlayerName?: string) => {
    const followUp = pendingFollowUpRef.current;
    if (!followUp) return;
    pendingFollowUpRef.current = null;

    setIsLoading(true);
    // Refresh roster context so newly-added players are visible to the next agent.
    await refreshTeamContextForAgent();
    const ctx = buildAgentContext();

    // Inject the resolved player name as extra system context so the
    // downstream agent can resolve pronouns ("him"/"her") without us
    // modifying the follow-up text (which would confuse the router).
    const playerHint = resolvedPlayerName
      ? `Note: "him"/"her" in this request refers to the player just added: ${resolvedPlayerName}.`
      : undefined;
    const ctxWithHint = playerHint
      ? { ...ctx, teamContextForAgent: ctx.teamContextForAgent ? `${ctx.teamContextForAgent}\n\n${playerHint}` : playerHint }
      : ctx;

    // If the router told us which agent handles the follow-up, dispatch directly
    // without re-routing (re-routing is what caused duplicate roster adds).
    const followUpAgent = pendingFollowUpAgentRef.current;
    pendingFollowUpAgentRef.current = null;
    if (followUpAgent) {
      const syntheticIntent: RouterIntent = {
        sub_agent: followUpAgent,
        action: 'handle',
        params: {},
        confidence: 1,
      };
      await dispatchRouterIntent(syntheticIntent, followUp, ctxWithHint);
      return;
    }

    if (isRouterEnabled()) {
      try {
        const intent = await routeUserMessage(followUp, ctxWithHint, null);
        if (intent) {
          await dispatchRouterIntent(intent, followUp, ctxWithHint);
          return;
        }
      } catch (err) {
        console.warn('Follow-up routing failed; falling back to help agent', err);
      }
    }

    await runHelpAgent(followUp, ctxWithHint);
    setIsLoading(false);
  };

  const dispatchRouterIntent = async (
    intent: RouterIntent,
    text: string,
    ctx: AgentRunContext
  ): Promise<boolean> => {
    // Store any secondary intent so it's processed after the primary flow completes.
    if (intent.follow_up) {
      pendingFollowUpRef.current = intent.follow_up;
      pendingFollowUpAgentRef.current = intent.follow_up_agent ?? null;
    }

    const requireTeam = (msg: string): boolean => {
      if (!currentTeam) {
        addMessage('assistant', msg);
        setIsLoading(false);
        return false;
      }
      return true;
    };

    switch (intent.sub_agent) {
      case 'practice': {
        if (!requireTeam('Please select a team first from the dashboard.')) return true;
        await practiceAgent.handle(text, ctx);
        setIsLoading(false);
        return true;
      }
      case 'gamePlan': {
        setIsLoading(false);
        if (!requireTeam('Please select a team first from the dashboard.')) return true;
        await gamePlanAgent.handle(text, ctx);
        return true;
      }
      case 'game': {
        setIsLoading(false);
        if (
          !requireTeam(
            'Please select a team first from the dashboard so I can add a game to their schedule.'
          )
        )
          return true;
        startCreateGameFlow(text, ctx);
        return true;
      }
      case 'formation': {
        setIsLoading(false);
        await formationAgent.handle(text, ctx);
        return true;
      }
      case 'depthChart': {
        setIsLoading(false);
        if (!requireTeam('Please select a team first from the dashboard.')) return true;
        await depthChartAgent.handle(text, ctx);
        return true;
      }
      case 'roster': {
        if (!requireTeam('Please select a team first from the dashboard.')) return true;
        const isInjury =
          intent.action === 'setInjuryStatus' ||
          intent.action === 'updateInjuryStatus' ||
          isSetInjuryIntent(text);
        if (isInjury) {
          await runInjuryStatusFlow(text, ctx);
        } else {
          await runAddPlayer(text, ctx, intent.params);
        }
        return true;
      }
      case 'stats': {
        if (!requireTeam('Please select a team first from the dashboard.')) return true;
        await statsAgent.handle(text, ctx);
        return true;
      }
      case 'attendance': {
        if (!requireTeam('Please select a team first from the dashboard.')) return true;
        await attendanceAgent.handle(text, ctx);
        return true;
      }
      case 'playbook':
      case 'help':
      case 'general_chat':
      default: {
        await runHelpAgent(text, ctx);
        return true;
      }
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput('');
    addMessage('user', text);
    setTimeout(() => inputRef.current?.focus(), 0);

    // Pending "add player" follow-up — unless they're clearly correcting to a practice request.
    if (pendingPlayerCreate && supersedesPendingPlayerCreateWithPracticeRequest(text)) {
      setPendingPlayerCreate(null);
    }

    if (pendingPlayerCreate) {
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        setPendingPlayerCreate(null);
        return;
      }
      const resolvedName = [pendingPlayerCreate.firstName, pendingPlayerCreate.lastName].filter(Boolean).join(' ') || undefined;
      const result = await continuePendingPlayerCreate(text, pendingPlayerCreate, buildAgentContext());
      if (result.kind === 'handled') await processQueuedFollowUp(resolvedName);
      return;
    }

    // Pending "what should we focus on?" follow-up after a placeholder practice.
    if (pendingPracticeForFocus) {
      const info = pendingPracticeForFocus;
      await continuePendingPracticeFocus(text, info, buildAgentContext());
      return;
    }

    // Add-drill follow-up ("which day?" or yes/no create practice).
    if (pendingAddDrillFollowUp) {
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        setPendingAddDrillFollowUp(null);
        return;
      }
      await continuePendingAddDrillFollowUp(text, buildAgentContext());
      return;
    }

    if (pendingGameLineupPick) {
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        setPendingGameLineupPick(null);
        return;
      }
      await continuePendingGameLineupPick(text, pendingGameLineupPick, buildAgentContext());
      return;
    }

    setIsLoading(true);

    // Orchestrator-level gate: checked once, before dispatching to ANY subagent
    // below (not just general Q&A), so the Rookie monthly limit actually applies
    // to every Coach Steve interaction.
    if (await hasReachedAiChatLimit()) {
      addMessage(
        'assistant',
        `You've used your **${ROOKIE_LIMITS.CHAT_MESSAGES} AI Coach messages** this month. Upgrade to **Pro** for unlimited AI Coach, AI-powered practice & game plans, lineup optimization, and more — or your limit resets next month.`
      );
      setIsLoading(false);
      return;
    }

    // Add-drill requests must not go through the LLM router: it often labels them as
    // general_chat, so only the help agent runs (fluent reply + no DB work). Regex is authoritative here.
    if (isAddDrillToPracticeIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        setIsLoading(false);
        return;
      }
      await practiceAgent.handle(text, buildAgentContext());
      await trackChatUsage();
      setIsLoading(false);
      return;
    }

    // Phase B: try the LLM router first when the feature flag is on.
    // Falls back to regex on parse failure or low confidence.
    if (isRouterEnabled()) {
      try {
        const ctx = buildAgentContext();
        const intent = await routeUserMessage(text, ctx, currentPendingFlowLabel());
        if (intent) {
          // Phase C: surface an interrupt prompt when the router signals
          // the user has clearly switched domains during a pending flow.
          const flowLabel = currentPendingFlowLabel();
          if (intent.interrupt && flowLabel) {
            setIsLoading(false);
            const candidateLabel = intent.clarification?.trim()
              ? intent.clarification.trim()
              : `${intent.sub_agent}.${intent.action}`;
            requestInterrupt({
              currentFlowLabel: flowLabel,
              candidateLabel,
              onDecline: () => {
                addMessage(
                  'assistant',
                  `OK — I’ll stay with **${flowLabel}**. Let me know what you want to do next.`
                );
              },
              onConfirm: async () => {
                cancelFlow();
                addMessage(
                  'assistant',
                  `Got it — I paused **${flowLabel}** and switched over.`
                );
                await dispatchRouterIntent(intent, text, buildAgentContext());
                await trackChatUsage();
              },
            });
            return;
          }
          await dispatchRouterIntent(intent, text, ctx);
          await trackChatUsage();
          return;
        }
      } catch (err) {
        // If the router itself blew up, log and fall through to regex.
        // eslint-disable-next-line no-console
        console.warn('Coach Steve router failed; falling back to regex', err);
      }
    }

    // Regex fallback. Intent order matters: check specific creation targets
    // first (practice, game plan, game, formation) so "create the formation"
    // is never mis-routed to add player or general AI.
    logRouted({
      text,
      source: 'regex',
      intent: null,
      routedTo: 'pre-dispatch',
    });
    if (isCreatePracticeIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        setIsLoading(false);
        return;
      }
      const ctx = buildAgentContext();
      await practiceAgent.handle(text, ctx);
      await trackChatUsage();
      setIsLoading(false);
      return;
    }
    if (isCreateGamePlanIntent(text)) {
      setIsLoading(false);
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        return;
      }
      await gamePlanAgent.handle(text, buildAgentContext());
      await trackChatUsage();
      return;
    }
    if (isCreateGameIntent(text)) {
      setIsLoading(false);
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard so I can add a game to their schedule.");
        return;
      }
      startCreateGameFlow(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // Create formation: direct to Depth Chart (formations are created there, not via chat)
    if (isCreateFormationIntent(text)) {
      setIsLoading(false);
      await formationAgent.handle(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // Assign player to formation (e.g. "put John at QB in Spread", "assign Ace to LDT in 4-3", or "assign Ace to the LDT position")
    if (isAssignPlayerToFormationIntent(text) || isGameLineupCoachIntent(text)) {
      setIsLoading(false);
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        return;
      }
      await depthChartAgent.handle(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // Set injury status flow (also continues a pending injury update).
    if (pendingInjuryUpdate || isSetInjuryIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        setIsLoading(false);
        return;
      }
      await runInjuryStatusFlow(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // Add player flow (creates a player + player card).
    if (isCreatePlayerIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        setIsLoading(false);
        return;
      }
      await runAddPlayer(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // Attendance lookup ("Who has missed the most practices?", "any
    // absences in the last 2 weeks?") — answered directly from attendance
    // records, otherwise falls through to the help agent. Checked BEFORE
    // stats: statsAgent's keyword regex is broad ("how many", "who has",
    // "most") and would otherwise swallow attendance questions first.
    if (isAttendanceLookupIntent(text) && currentTeam) {
      await attendanceAgent.handle(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // Stats lookup ("Who has the highest batting average?") — answered
    // directly when possible, otherwise falls through to the help agent.
    if (isStatsLookupIntent(text) && currentTeam) {
      await statsAgent.handle(text, buildAgentContext());
      await trackChatUsage();
      return;
    }

    // General question fallback — Coach Steve agent with live team/roster context.
    await runHelpAgent(text, buildAgentContext());
    await trackChatUsage();
  };

  const noTeam = !currentTeam;

  const panelHeader = isPanel && (
    <div
      className="flex-shrink-0 flex items-center justify-between gap-3 px-4 pb-3 border-b border-stone-200 bg-white"
      style={{ paddingTop: 'calc(0.75rem + var(--safe-area-inset-top, 0px))' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <MessageCircle className="h-5 w-5 flex-shrink-0 text-primary" />
        <span className="font-semibold text-dark truncate">Coach Steve</span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors flex-shrink-0"
        aria-label="Close panel"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  const content = (
    <>
      {!isPanel && (
        <PageHeader
          title="Coach Steve"
          icon={<MessageCircle className="h-6 w-6" />}
        />
      )}
      {noTeam ? (
        <div className={`rounded-2xl border border-stone-200 bg-white p-6 text-center ${isPanel ? 'm-4' : ''}`}>
          <p className="text-stone-600 mb-4">Select a team from the dashboard so Coach Steve can create practices and game plans for that team.</p>
          <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2">
            Go to dashboard
          </Link>
        </div>
      ) : (
        <>
          <div className={`rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden flex flex-col ${isPanel ? 'm-0 rounded-none border-0 border-b border-stone-200 flex-1 min-h-0' : ''}`}>
            {!isPanel && (
              <div className="p-4 border-b border-stone-100 bg-stone-50/50">
                <p className="text-sm text-stone-600">
                  Browsing as <strong className="text-dark">{currentTeam.name}</strong>
                </p>
              </div>
            )}
            {isPanel && (
              <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/50">
                <p className="text-sm text-stone-600">
                  Browsing as <strong className="text-dark">{currentTeam.name}</strong>
                </p>
              </div>
            )}

            <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isPanel ? 'min-h-0' : 'min-h-[320px] max-h-[50vh]'}`}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-stone-100 text-dark'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {msg.content.split('**').map((part, i) =>
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </p>
                    {msg.link && (
                      <Link
                        to={msg.link.href}
                        onClick={() => { if (isPanel && onClose) onClose(); }}
                        className="mt-2 inline-flex items-center gap-1 text-sm font-medium underline opacity-90 hover:opacity-100"
                      >
                        {msg.link.label} →
                      </Link>
                    )}
                  </div>
                </div>
              ))}

              {pendingInterrupt && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 space-y-2">
                    <p className="text-sm text-dark">
                      You're in the middle of <strong>{pendingInterrupt.currentFlowLabel}</strong>.
                      Want to pause that and switch to <strong>{pendingInterrupt.candidateLabel}</strong>?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-primary text-xs px-3 py-1.5"
                        onClick={() => confirmInterrupt(true)}
                      >
                        Switch
                      </button>
                      <button
                        type="button"
                        className="text-xs px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-100"
                        onClick={() => confirmInterrupt(false)}
                      >
                        Stay
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {pendingAction === 'game_plan' && currentTeam?.sport === 'Football' && (
                <div className="flex justify-start">
                  <div className="max-w-full w-full rounded-2xl bg-stone-50 border border-stone-200 p-4">
                    <CoachSteveGamePlanForm
                      teamId={currentTeam.id}
                      teamName={currentTeam.name}
                      onSuccess={(result) => handleFormSuccess('game_plan', result)}
                      onCancel={handleFormCancel}
                    />
                  </div>
                </div>
              )}

              {pendingAction === 'create_game' && currentTeam && (
                <div className="flex justify-start">
                  <div className="max-w-full w-full">
                    <CoachSteveCreateGameForm
                      teamId={currentTeam.id}
                      initialValues={createGameInitialValues ?? undefined}
                      onSuccess={(game: Game) => {
                        setPendingAction(null);
                        setCreateGameInitialValues(null);
                        addMessage('assistant', `Done. I've added the game vs **${game.opponent_name}** (${game.date}).`, {
                          label: 'Open game',
                          href: `/games/${game.id}`,
                        });
                      }}
                      onCancel={() => {
                        setPendingAction(null);
                        setCreateGameInitialValues(null);
                      }}
                    />
                  </div>
                </div>
              )}

              {generationProgress && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl bg-primary bg-opacity-10 border border-primary border-opacity-20 px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary" />
                    <p className="text-sm text-dark">
                      {generationProgress.split('**').map((part, i) =>
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </p>
                  </div>
                </div>
              )}

              {isLoading && !generationProgress && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-stone-100 px-4 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-stone-100 space-y-3">
              <div className="flex gap-2 flex-wrap">
                {currentTeam?.sport !== 'Football' && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!currentTeam) {
                        addMessage('assistant', "Please select a team first from the dashboard so I can add a game to their schedule.");
                        return;
                      }
                      addMessage('user', 'Create a game');
                      addMessage('assistant', "Sure — fill in the details below to add a game to the schedule.");
                      setCreateGameInitialValues(null);
                      setPendingAction('create_game');
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary bg-opacity-10 text-primary hover:bg-primary hover:text-white transition-colors"
                  >
                    <CalendarPlus className="h-4 w-4" />
                    Create game
                  </button>
                )}
                {currentTeam?.sport === 'Football' && (
                  <button
                    type="button"
                    onClick={() => handleQuickAction('game_plan')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary bg-opacity-10 text-primary hover:bg-primary hover:text-white transition-colors"
                  >
                    <Target className="h-4 w-4" />
                    Create game plan
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    pendingPracticeForFocus
                      ? 'e.g. tackling, red zone, or tempo…'
                      : pendingAddDrillFollowUp?.phase === 'needDate'
                        ? 'Which day? e.g. tomorrow or Tuesday…'
                        : pendingAddDrillFollowUp?.phase === 'awaitCreateConfirm'
                          ? 'yes or no…'
                          : 'Describe what you want (e.g. practice plan, game plan, or create a game)…'
                  }
                  className="flex-1 min-h-[44px] max-h-32 rounded-xl border border-stone-200 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={2}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="self-end p-2.5 rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (isPanel) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-cream">
        {panelHeader}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {content}
        </div>
      </div>
    );
  }
  return <div className="max-w-3xl mx-auto">{content}</div>;
};

export default CoachSteve;
